import React, { useEffect, useRef } from "react";

/**
 * InterviewAvatar - cinematic AI interviewer for the Official AI Interview.
 * Purely a visual layer: lip-sync is driven in real time by the EXISTING TTS
 * audio element (Web Audio analyser), with natural blinking, breathing and
 * subtle head movement. Speaking / listening states mirror the interview flow.
 */

const INTERVIEWERS = {
  hr_officer: { name: "Amelia Santos", role: "HR Manager" },
  hiring_manager: { name: "Daniel Mercer", role: "Line Manager" },
  executive: { name: "Richard Hale", role: "Senior Director" },
  recruiter: { name: "Claire Bennett", role: "Executive Recruiter" },
  tech_lead: { name: "David Chen", role: "Engineering Lead" },
  panel: { name: "Miranda Cole", role: "Panel Lead" },
};

const FRAME_STATES = ["base", "half", "open", "o", "ee", "m", "blink"];

// Personas with the extended 10-frame mouth set (pilot: hiring_manager).
// v2 sets add "part" (barely parted), "mid" (medium open) and "f" (teeth on
// lip) frames, composited mouth-only onto the SAME base photo so nothing but
// the lips/jaw ever changes between frames.
const V2_SET = new Set(["hiring_manager", "hr_officer", "executive", "recruiter", "tech_lead", "panel"]);
const FRAME_STATES_V2 = ["base", "rest", "part", "half", "mid", "open", "o", "oo", "ee", "m", "f", "blink"];

// Mouth-shape targets per viseme class; `env` (spectral intensity 0..1)
// scales openness so loud syllables articulate more than soft ones.
const REST_T = { rest: 0, part: 0, half: 0, mid: 0, open: 0, o: 0, oo: 0, ee: 0, m: 0, f: 0 };

// v2 openness ladder: crossfade between two ADJACENT full-opacity frames so
// the dominant mouth shape is always unmistakable (never a muddy 40% blend
// over the closed base photo).
const V2_LADDER = ["part", "half", "mid", "open"];
function ladderTargets(pos) {
  const p = clamp01(pos) * (V2_LADDER.length - 1);
  const i = Math.min(Math.floor(p), V2_LADDER.length - 2);
  const f = p - i;
  return { ...REST_T, [V2_LADDER[i]]: 1, [V2_LADDER[i + 1]]: f };
}

function visemeTargets(v, env, v2) {
  const e = 0.55 + 0.45 * env;
  if (v2) {
    switch (v) {
      case "A": return ladderTargets(0.25 + 0.55 * env);
      case "E": return { ...REST_T, ee: 1 };
      case "O": { const w = clamp01((env - 0.3) * 2); return { ...REST_T, o: 1, oo: 1 - w }; }
      case "F": return { ...REST_T, f: 1 };
      case "S": return { ...REST_T, part: 0.9, ee: 0.3 };
      case "T": return { ...REST_T, part: 1, half: 0.35 };
      case "M": return { ...REST_T, m: 1 };
      default: return { ...REST_T, rest: 0.85 };
    }
  }
  switch (v) {
    case "A": return { ...REST_T, half: 0.65, open: 0.4 + 0.6 * env };
    case "E": return { ...REST_T, half: 0.25, ee: 0.92 * e };
    case "O": return { ...REST_T, half: 0.2, o: 0.95 * e };
    case "F": return { ...REST_T, half: 0.85 };
    case "S": return { ...REST_T, half: 0.55 + 0.2 * env, ee: 0.15 };
    case "T": return { ...REST_T, half: 0.45 + 0.2 * env };
    case "M": return { ...REST_T, m: 1 };
    default: return REST_T;
  }
}

const CSS = `
@keyframes iv-sway { 0%,100% { transform: translate3d(0,0,0) rotate(0deg) scale(1.02); }
  30% { transform: translate3d(1.6px,1.8px,0) rotate(0.28deg) scale(1.024); }
  65% { transform: translate3d(-1.8px,0.9px,0) rotate(-0.32deg) scale(1.028); } }
@keyframes iv-nod { 0% { transform: translateY(0) scale(1.02); } 35% { transform: translateY(4px) rotate(0.35deg) scale(1.02); }
  70% { transform: translateY(0.5px) scale(1.02); } 100% { transform: translateY(0) scale(1.02); } }
@keyframes iv-bar { 0%,100% { height: 4px; } 50% { height: 13px; } }
@keyframes iv-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
.iv-stage { animation: iv-sway 11s ease-in-out infinite; will-change: transform; }
.iv-stage.iv-speaking { animation-duration: 7s; }
.iv-stage.iv-nodding { animation: iv-nod 1.1s ease-in-out; }
.iv-frame { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: center 22%; }
.iv-eq { display: inline-flex; align-items: flex-end; gap: 2px; height: 14px; }
.iv-eq span { width: 2.5px; border-radius: 2px; background: currentColor; animation: iv-bar 0.9s ease-in-out infinite; }
.iv-eq span:nth-child(2) { animation-delay: 0.18s; } .iv-eq span:nth-child(3) { animation-delay: 0.36s; }
`;

function injectCss() {
  if (document.getElementById("iv-avatar-css")) return;
  const el = document.createElement("style");
  el.id = "iv-avatar-css";
  el.textContent = CSS;
  document.head.appendChild(el);
}

const clamp01 = (v) => Math.max(0, Math.min(1, v));

export default function InterviewAvatar({ personaId, speaking, listening, audioRef, visemes, mouthGated }) {
  const id = INTERVIEWERS[personaId] ? personaId : "hiring_manager";
  const who = INTERVIEWERS[id];
  const v2 = V2_SET.has(id);
  const av = (s) => `/avatars/${id}${v2 ? "_v2" : ""}_${s}.webp`;
  const halfRef = useRef(null);
  const openRef = useRef(null);
  const oRef = useRef(null);
  const eeRef = useRef(null);
  const mRef = useRef(null);
  const partRef = useRef(null);
  const midRef = useRef(null);
  const fRef = useRef(null);
  const restRef = useRef(null);
  const ooRef = useRef(null);
  const blinkRef = useRef(null);
  const stageRef = useRef(null);
  const innerRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(0);
  const levelRef = useRef(0);
  const speakingRef = useRef(false);
  const visemesRef = useRef(null);
  const vPtrRef = useRef(0);
  speakingRef.current = !!speaking;
  useEffect(() => { visemesRef.current = visemes || null; vPtrRef.current = 0; }, [visemes]);
  const gatedRef = useRef(false);
  useEffect(() => { gatedRef.current = !!mouthGated; }, [mouthGated]);

  useEffect(() => { injectCss(); }, []);

  // Preload all frames for this persona so crossfades never flash.
  useEffect(() => {
    (v2 ? FRAME_STATES_V2 : FRAME_STATES).forEach((s) => { const im = new Image(); im.src = av(s); });
  }, [id]);

  // Wire the existing TTS <audio> element into a Web Audio analyser.
  // Only attach when the context is confirmed RUNNING - a suspended context
  // would silence the element, and audibility of the question is sacred.
  useEffect(() => {
    const el = audioRef?.current;
    if (!el) return;
    let disposed = false;
    const tryAttach = async () => {
      if (disposed || el.__ivBundle) { analyserRef.current = el.__ivBundle?.analyser || analyserRef.current; return; }
      if (el.__ivAttaching) return;
      el.__ivAttaching = true;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        try { await ctx.resume(); } catch { /* retry on next event */ }
        if (ctx.state !== "running") { await ctx.close().catch(() => {}); return; }
        const source = ctx.createMediaElementSource(el);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.35;
        const gain = ctx.createGain();
        gain.gain.value = 1.25; // keep the interviewer clearly audible
        source.connect(analyser);
        analyser.connect(gain);
        gain.connect(ctx.destination);
        el.__ivBundle = { ctx, analyser };
        analyserRef.current = analyser;
      } catch {
        /* graceful fallback: rhythm-driven lips */
      } finally {
        el.__ivAttaching = false;
      }
    };
    if (el.__ivBundle) analyserRef.current = el.__ivBundle.analyser;
    const onPlay = () => { tryAttach(); el.__ivBundle?.ctx?.resume?.().catch(() => {}); };
    const onPointer = () => { tryAttach(); el.__ivBundle?.ctx?.resume?.().catch(() => {}); };
    el.addEventListener("play", onPlay);
    document.addEventListener("pointerdown", onPointer);
    tryAttach();
    return () => {
      disposed = true;
      el.removeEventListener("play", onPlay);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [audioRef]);

  // Lip-sync loop - hybrid engine.
  // PRIMARY: precomputed viseme timeline (Whisper word timestamps → phonemes)
  // indexed by audio.currentTime for frame-accurate shape selection with
  // coarticulation blending into the next viseme. SECONDARY: live spectral
  // analysis supplies intensity (loud syllables open more) and remains the
  // full fallback while a timeline is still being prepared.
  useEffect(() => {
    const freq = new Uint8Array(512);
    const state = { open: 0, peak: 0.15, sil: 0, cur: { ...REST_T } };
    const el = () => audioRef?.current;
    const findSeg = (tl, at) => {
      let i = vPtrRef.current;
      if (i >= tl.length || tl[i][0] > at) i = 0;
      while (i < tl.length && tl[i][1] < at) i++;
      vPtrRef.current = Math.max(0, i - 1);
      if (i < tl.length && tl[i][0] <= at && at <= tl[i][1]) return i;
      return -1;
    };
    const tick = (t) => {
      // --- spectral intensity (env 0..1) ---
      let env = 0, fricCap = 1, norm = 0;
      if (speakingRef.current) {
        const analyser = analyserRef.current;
        if (analyser) {
          analyser.getByteFrequencyData(freq);
          let low = 0, mid = 0, high = 0;
          for (let i = 2; i < 21; i++) low += freq[i];
          for (let i = 21; i < 64; i++) mid += freq[i];
          for (let i = 64; i < 150; i++) high += freq[i];
          low /= 19; mid /= 43; high /= 86;
          const e0 = (low + mid * 0.75) / 255;
          state.peak = Math.max(e0, state.peak * 0.996, 0.12);
          norm = clamp01(e0 / state.peak);
          const fric = high / (low + mid + high + 1);
          if (fric > 0.42) fricCap = clamp01(0.34 + (0.62 - fric) * 1.2);
          env = norm;
          if (norm < 0.07) state.sil = Math.min(state.sil + 1, 30);
          else state.sil = 0;
        } else {
          env = 0.35 + 0.3 * Math.abs(Math.sin(t / 96));
        }
      } else {
        state.sil = 30;
      }
      // --- shape targets ---
      let tg = REST_T;
      const tl = visemesRef.current;
      const audio = el();
      if (speakingRef.current && tl && tl.length && audio) {
        // lookahead compensates opacity smoothing so lips land on time
        const at = audio.currentTime + 0.045;
        const idx = findSeg(tl, at);
        if (idx < 0 && v2) tg = { ...REST_T, rest: 0.85 };
        if (idx >= 0 && (analyserRef.current ? state.sil < 12 : true)) {
          const seg = tl[idx];
          tg = { ...visemeTargets(seg[2], env, v2) };
          const nxt = tl[idx + 1];
          if (nxt && nxt[0] - seg[1] < 0.09) {
            const p = (at - seg[0]) / Math.max(seg[1] - seg[0], 0.001);
            if (p > 0.6) {
              const nt = visemeTargets(nxt[2], env, v2);
              const b = Math.min((p - 0.6) / 0.4, 1);
              for (const k in tg) tg[k] = tg[k] * (1 - b) + nt[k] * b;
            }
          }
        }
      } else if (speakingRef.current) {
        // fallback: spectral-only engine (identical to previous behavior)
        let target = Math.pow(norm, 1.15);
        if (!analyserRef.current) target = env;
        if (target > 0.45) target *= 0.85 + 0.15 * (0.5 + 0.5 * Math.sin(t / 24));
        if (state.sil > 0) target = 0;
        target = Math.min(target, fricCap);
        // While the precise timeline is still being prepared, gate the mouth
        // to subtle micro-motion so imprecise flapping never plays first.
        if (gatedRef.current) target *= 0.12;
        const k0 = target > state.open ? 0.65 : state.sil > 2 ? 0.55 : 0.32;
        state.open += (target - state.open) * k0;
        tg = v2
          ? (state.open < 0.04 ? { ...REST_T, rest: 0.85 } : ladderTargets(state.open * 0.95))
          : { ...REST_T, half: clamp01(state.open * 2.2), open: clamp01((state.open - 0.36) * 1.8) };
      }
      // --- per-layer smoothing: fast attack, gentle release = no flicker ---
      const cur = state.cur;
      const refs = { rest: restRef, part: partRef, half: halfRef, mid: midRef, open: openRef, o: oRef, oo: ooRef, ee: eeRef, m: mRef, f: fRef };
      let energy = 0;
      for (const k in cur) {
        const tv = tg[k] || 0;
        cur[k] += (tv - cur[k]) * (tv > cur[k] ? 0.55 : 0.38);
        if (cur[k] < 0.005) cur[k] = 0;
        if (refs[k].current) refs[k].current.style.opacity = cur[k];
        if (k !== "m" && k !== "part" && k !== "rest") energy = Math.max(energy, cur[k]);
      }
      levelRef.current = energy;
      // speech-driven micro emphasis - a real speaker's head moves with stress
      if (innerRef.current) {
        innerRef.current.style.transform = `translateY(${(-energy * 1.6).toFixed(2)}px) scale(${(1 + energy * 0.004).toFixed(4)})`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [audioRef, v2]);

  // Natural blinking.
  useEffect(() => {
    let alive = true;
    let t1, t2;
    const loop = () => {
      t1 = setTimeout(() => {
        if (!alive) return;
        if (blinkRef.current) blinkRef.current.style.opacity = 1;
        t2 = setTimeout(() => {
          if (blinkRef.current) blinkRef.current.style.opacity = 0;
          if (alive) loop();
        }, 140);
      }, 2600 + Math.random() * 3200);
    };
    loop();
    return () => { alive = false; clearTimeout(t1); clearTimeout(t2); };
  }, [id]);

  // Attentive nods while the candidate is answering.
  useEffect(() => {
    if (!listening) return;
    let alive = true;
    let t;
    const nod = () => {
      t = setTimeout(() => {
        if (!alive) return;
        const el = stageRef.current;
        if (el) {
          el.classList.add("iv-nodding");
          setTimeout(() => el.classList.remove("iv-nodding"), 1150);
        }
        nod();
      }, 5200 + Math.random() * 4200);
    };
    nod();
    return () => { alive = false; clearTimeout(t); };
  }, [listening]);

  const status = speaking
    ? { label: "Speaking", cls: "bg-amber-500/15 border-amber-500/40 text-amber-300" }
    : listening
    ? { label: "Listening", cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" }
    : { label: "Ready", cls: "bg-zinc-800/80 border-zinc-700 text-zinc-400" };

  return (
    <div
      data-testid="interview-avatar"
      className="relative rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950 aspect-[4/3] shadow-[0_0_60px_-24px_rgba(245,158,11,0.25)]"
    >
      <div ref={stageRef} className={`iv-stage absolute inset-0 ${speaking ? "iv-speaking" : ""}`}>
        <div ref={innerRef} className="absolute inset-0" style={{ willChange: "transform" }}>
          <img className="iv-frame" src={av("base")} alt="" draggable={false} />
          {v2 && <img ref={restRef} className="iv-frame" src={av("rest")} alt="" draggable={false} style={{ opacity: 0 }} />}
          {v2 && <img ref={partRef} className="iv-frame" src={av("part")} alt="" draggable={false} style={{ opacity: 0 }} />}
          <img ref={halfRef} className="iv-frame" src={av("half")} alt="" draggable={false} style={{ opacity: 0 }} />
          {v2 && <img ref={midRef} className="iv-frame" src={av("mid")} alt="" draggable={false} style={{ opacity: 0 }} />}
          <img ref={openRef} className="iv-frame" src={av("open")} alt="" draggable={false} style={{ opacity: 0 }} />
          <img ref={oRef} className="iv-frame" src={av("o")} alt="" draggable={false} style={{ opacity: 0 }} />
          {v2 && <img ref={ooRef} className="iv-frame" src={av("oo")} alt="" draggable={false} style={{ opacity: 0 }} />}
          <img ref={eeRef} className="iv-frame" src={av("ee")} alt="" draggable={false} style={{ opacity: 0 }} />
          <img ref={mRef} className="iv-frame" src={av("m")} alt="" draggable={false} style={{ opacity: 0 }} />
          {v2 && <img ref={fRef} className="iv-frame" src={av("f")} alt="" draggable={false} style={{ opacity: 0 }} />}
          <img ref={blinkRef} className="iv-frame" src={av("blink")} alt="" draggable={false}
               style={{ opacity: 0, transition: "opacity 70ms linear" }} />
        </div>
      </div>
      {/* cinematic vignette */}
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: "radial-gradient(120% 90% at 50% 30%, transparent 55%, rgba(9,9,11,0.55) 100%)" }} />
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-zinc-950/90 to-transparent pointer-events-none" />
      {/* status chip */}
      <div data-testid="avatar-status"
           className={`absolute top-3 right-3 inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest backdrop-blur-sm ${status.cls}`}>
        {speaking ? (
          <span className="iv-eq text-amber-400"><span /><span /><span /></span>
        ) : listening ? (
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" style={{ animation: "iv-pulse 1.4s ease-in-out infinite" }} />
        ) : (
          <span className="h-1.5 w-1.5 rounded-full bg-zinc-500" />
        )}
        {status.label}
      </div>
      {/* name plate */}
      <div className="absolute left-4 bottom-3" data-testid="avatar-name">
        <div className="text-sm font-semibold text-zinc-50 drop-shadow">{who.name}</div>
        <div className="text-[11px] text-zinc-400 tracking-wide">{who.role} · Intuiterview Interview Panel</div>
      </div>
    </div>
  );
}
